import {
  Component,
  inject,
  input,
  output,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
} from '@angular/core';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { FormsModule } from '@angular/forms';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import {
  CustomSmsParameterDto,
  CustomSmsParameterDtoPlaceEnum,
  CustomSmsParameterDtoTypeEnum,
  CustomSmsSettings,
  CustomSmsSettingsAuthMethodEnum,
} from '@tourfold/libraries/tf-api-client-public-v2';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  buildErrorTranslationKey,
  extractServerErrors,
  SignalFormServerErrors,
} from '@tourfold/libraries/shared-util';

const PARAMETER_POINTER_PATTERN = /^#?\/?customConfig\/parameters\/(\d+)\/(\w+)$/;
const FALLBACK_VALIDATION_KEY = 'SERVER_ERRORS.FIELD_VALIDATION_ERROR';

// Mirrors the backend's SmsGatewayValidator.HTTP_URL_REGEX exactly so client and server enforce
// the same rule: an http(s) scheme followed by a non-empty authority (rejects https:///path,
// https://, whitespace hosts). The authority class allows {} so templated path params still pass.
const URL_PATTERN = /^https?:\/\/[^/\s?#]+([/?#].*)?$/i;
const URL_INVALID_KEY = 'SETTINGS_SMS.GATEWAY.CUSTOM_CONFIGURATION.URL_INVALID';

interface CustomParameterWithId extends CustomSmsParameterDto {
  id: string;
}

interface CustomFormModel {
  name: string;
  enabled: boolean;
  url: string;
  httpMethod: string;
  contentType: string;
  authMethod: CustomSmsSettingsAuthMethodEnum;
  basicUsername: string;
  basicPassword: string;
  oauthUrl: string;
  oauthClientId: string;
  oauthClientSecret: string;
  oauthScope: string;
  oauthGrantType: string;
  apiKeyHeaderName: string;
  apiKeyHeaderValue: string;
}

const EMPTY_CUSTOM_FORM: CustomFormModel = {
  name: '',
  enabled: true,
  url: '',
  httpMethod: 'POST',
  contentType: 'application/json',
  authMethod: CustomSmsSettingsAuthMethodEnum.None,
  basicUsername: '',
  basicPassword: '',
  oauthUrl: '',
  oauthClientId: '',
  oauthClientSecret: '',
  oauthScope: '',
  oauthGrantType: '',
  apiKeyHeaderName: '',
  apiKeyHeaderValue: '',
};

const CUSTOM_FORM_FIELDS = [
  'name',
  'url',
  'basicUsername',
  'basicPassword',
  'oauthUrl',
  'oauthClientId',
  'oauthClientSecret',
  'oauthScope',
  'oauthGrantType',
  'apiKeyHeaderName',
  'apiKeyHeaderValue',
] as const satisfies readonly (keyof CustomFormModel)[];

export type CustomSavePayload = {
  name: string;
  enabled: boolean;
  config: CustomSmsSettings;
};

@Component({
  selector: 'tf-settings-custom-sms-config-dialog',
  imports: [
    DialogModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    FormsModule,
    ToggleSwitchModule,
    TranslateModule,
  ],
  templateUrl: './custom-sms-config-dialog.html',
  styleUrl: './custom-sms-config-dialog.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomConfigDialog {
  readonly visible = input<boolean>(false);
  readonly isEditingSystem = input<boolean>(false);
  readonly initialName = input<string>('');
  readonly initialEnabled = input<boolean>(true);
  readonly initialConfig = input<Partial<CustomSmsSettings> | null>(null);
  readonly initialParameters = input<CustomSmsParameterDto[]>([]);
  readonly onSave = input.required<(payload: CustomSavePayload) => Promise<void>>();

  readonly visibleChange = output<boolean>();

  private readonly translate = inject(TranslateService);

  readonly formModel = signal<CustomFormModel>({ ...EMPTY_CUSTOM_FORM });
  readonly parameters = signal<CustomParameterWithId[]>([]);
  readonly isSaving = signal<boolean>(false);

  /**
   * Per-parameter validation errors, keyed by parameter id and then by field name
   * (`key`, `type`, `place`, `staticValue`). Filled from RFC 9457 pointers like
   * `#/customConfig/parameters/<index>/<field>` after a save fails.
   */
  readonly parameterErrors = signal<Record<string, Record<string, string>>>({});

  readonly serverErrors = new SignalFormServerErrors(this.formModel, CUSTOM_FORM_FIELDS, {
    pointerToField: {
      'customConfig/url': 'url',
      'customConfig/basic/username': 'basicUsername',
      'customConfig/basic/password': 'basicPassword',
      'customConfig/oauth/url': 'oauthUrl',
      'customConfig/oauth/clientId': 'oauthClientId',
      'customConfig/oauth/clientSecret': 'oauthClientSecret',
      'customConfig/oauth/scope': 'oauthScope',
      'customConfig/oauth/grantType': 'oauthGrantType',
      'customConfig/apiKey/headerName': 'apiKeyHeaderName',
      'customConfig/apiKey/headerValue': 'apiKeyHeaderValue',
    },
  });

  readonly httpMethodOptions = ['POST', 'GET', 'PUT', 'PATCH'];
  readonly contentTypeOptions = [
    'application/json',
    'application/x-www-form-urlencoded',
    'multipart/form-data',
    'text/plain',
  ];
  readonly authenticationOptions = Object.values(CustomSmsSettingsAuthMethodEnum);
  readonly parameterTypeOptions = Object.values(CustomSmsParameterDtoTypeEnum);
  readonly parameterLocationOptions = Object.values(CustomSmsParameterDtoPlaceEnum);

  /**
   * Client-side URL format error for the API endpoint, shown inline so the user knows why Save
   * is disabled before hitting the server. Only surfaces once the field is non-empty.
   */
  readonly urlClientError = computed<string | undefined>(() => {
    const url = this.formModel().url;
    return url.length > 0 && !URL_PATTERN.test(url) ? URL_INVALID_KEY : undefined;
  });

  /** Client-side URL format error for the OAuth token URL (only relevant when OAUTH is selected). */
  readonly oauthUrlClientError = computed<string | undefined>(() => {
    const m = this.formModel();
    if (m.authMethod !== CustomSmsSettingsAuthMethodEnum.Oauth) return undefined;
    return m.oauthUrl.length > 0 && !URL_PATTERN.test(m.oauthUrl) ? URL_INVALID_KEY : undefined;
  });

  readonly isValid = computed(() => {
    const m = this.formModel();
    const hasName = m.name.trim().length > 0;
    const hasEndpoint = URL_PATTERN.test(m.url);
    const isBasicAuth = m.authMethod === CustomSmsSettingsAuthMethodEnum.Basic;
    const hasBasicAuthFields = !isBasicAuth || (!!m.basicUsername && !!m.basicPassword);
    const isOAuth = m.authMethod === CustomSmsSettingsAuthMethodEnum.Oauth;
    const hasOAuthFields =
      !isOAuth || (URL_PATTERN.test(m.oauthUrl) && !!m.oauthClientId && !!m.oauthClientSecret);
    const isApiKey = m.authMethod === CustomSmsSettingsAuthMethodEnum.ApiKeyHeader;
    const hasApiKeyFields = !isApiKey || (!!m.apiKeyHeaderName && !!m.apiKeyHeaderValue);
    const areParametersValid = this.parameters().every((p) => !!p.key && !!p.type && !!p.place);
    return (
      hasName &&
      hasEndpoint &&
      hasBasicAuthFields &&
      hasOAuthFields &&
      hasApiKeyFields &&
      areParametersValid
    );
  });

  constructor() {
    // Re-seed the form model and parameters whenever the dialog opens.
    effect(() => {
      const initial = this.initialConfig();
      const initialName = this.initialName();
      const initialEnabled = this.initialEnabled();
      const initialParams = this.initialParameters();
      const isVisible = this.visible();
      if (isVisible) {
        this.formModel.set({
          name: initialName ?? '',
          enabled: initialEnabled ?? true,
          url: initial?.url ?? '',
          httpMethod: initial?.httpMethod ?? 'POST',
          contentType: initial?.contentType ?? 'application/json',
          authMethod: initial?.authMethod ?? CustomSmsSettingsAuthMethodEnum.None,
          basicUsername: initial?.basic?.username ?? '',
          basicPassword: initial?.basic?.password ?? '',
          oauthUrl: initial?.oauth?.url ?? '',
          oauthClientId: initial?.oauth?.clientId ?? '',
          oauthClientSecret: initial?.oauth?.clientSecret ?? '',
          oauthScope: initial?.oauth?.scope ?? '',
          oauthGrantType: initial?.oauth?.grantType ?? '',
          apiKeyHeaderName: initial?.apiKey?.headerName ?? '',
          apiKeyHeaderValue: initial?.apiKey?.headerValue ?? '',
        });

        const params = initial?.parameters ?? initialParams ?? [];
        this.parameters.set(
          params.map((p, index) => ({
            ...p,
            id: `${p.key ?? 'param'}-${index}-${Date.now()}`,
            staticValue: p.staticValue ?? '',
          }))
        );
        this.serverErrors.clear();
        this.parameterErrors.set({});
      }
    });
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.formModel.set({ ...EMPTY_CUSTOM_FORM });
      this.parameters.set([]);
      this.serverErrors.clear();
      this.parameterErrors.set({});
    }
    this.visibleChange.emit(visible);
  }

  paramError(paramId: string, field: string): string | undefined {
    return this.parameterErrors()[paramId]?.[field];
  }

  private clearParameterFieldError(paramId: string, field: string): void {
    this.parameterErrors.update((current) => {
      if (!current[paramId]?.[field]) return current;
      const remainingFields = { ...current[paramId] };
      delete remainingFields[field];
      const next = { ...current };
      if (Object.keys(remainingFields).length === 0) {
        delete next[paramId];
      } else {
        next[paramId] = remainingFields;
      }
      return next;
    });
  }

  updateField<K extends keyof CustomFormModel>(field: K, value: CustomFormModel[K]): void {
    this.formModel.update((current) => ({ ...current, [field]: value }));
  }

  onAuthMethodChange(value: CustomSmsSettingsAuthMethodEnum | null): void {
    this.updateField('authMethod', value ?? CustomSmsSettingsAuthMethodEnum.None);
  }

  addParameter(): void {
    const newParameter: CustomParameterWithId = {
      id:
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Date.now().toString(),
      key: '',
      type: CustomSmsParameterDtoTypeEnum.Extra,
      place: CustomSmsParameterDtoPlaceEnum.Query,
      staticValue: '',
    };
    this.parameters.set([...this.parameters(), newParameter]);
  }

  removeParameter(id: string): void {
    this.parameters.set(this.parameters().filter((p) => p.id !== id));
    this.parameterErrors.update((current) => {
      if (!current[id]) return current;
      const next = { ...current };
      delete next[id];
      return next;
    });
  }

  onParameterFieldChange(id: string, field: string, value: string | boolean): void {
    this.parameters.set(this.parameters().map((p) => (p.id === id ? { ...p, [field]: value } : p)));
    this.clearParameterFieldError(id, field);
  }

  async handleSave(): Promise<void> {
    if (!this.isValid() || this.isSaving()) {
      return;
    }
    const m = this.formModel();
    const parameters: CustomSmsParameterDto[] = this.parameters().map(
      ({ id: _id, ...rest }) => rest
    );
    const config: CustomSmsSettings = {
      url: m.url,
      httpMethod: m.httpMethod,
      contentType: m.contentType,
      authMethod: m.authMethod,
      parameters,
      basic:
        m.authMethod === CustomSmsSettingsAuthMethodEnum.Basic
          ? { username: m.basicUsername, password: m.basicPassword }
          : undefined,
      oauth:
        m.authMethod === CustomSmsSettingsAuthMethodEnum.Oauth
          ? {
              url: m.oauthUrl,
              clientId: m.oauthClientId,
              clientSecret: m.oauthClientSecret,
              scope: m.oauthScope,
              grantType: m.oauthGrantType,
            }
          : undefined,
      apiKey:
        m.authMethod === CustomSmsSettingsAuthMethodEnum.ApiKeyHeader
          ? { headerName: m.apiKeyHeaderName, headerValue: m.apiKeyHeaderValue }
          : undefined,
    };
    const payload: CustomSavePayload = { name: m.name, enabled: m.enabled, config };

    this.isSaving.set(true);
    this.serverErrors.clear();
    this.parameterErrors.set({});
    try {
      await this.onSave()(payload);
      // success: parent closes the dialog via [visible]
    } catch (err) {
      await this.serverErrors.apply(err);
      await this.applyParameterErrors(err);
    } finally {
      this.isSaving.set(false);
    }
  }

  /**
   * Maps RFC 9457 errors with pointers like `#/customConfig/parameters/0/key`
   * into per-parameter error messages keyed by the parameter's stable id.
   * Top-level pointers are handled by {@link serverErrors} and ignored here.
   */
  private async applyParameterErrors(err: unknown): Promise<void> {
    const errors = await extractServerErrors(err);
    if (errors.length === 0) return;
    const params = this.parameters();
    const result: Record<string, Record<string, string>> = {};
    for (const e of errors) {
      const match = e.pointer?.match(PARAMETER_POINTER_PATTERN);
      if (!match) continue;
      const indexStr = match[1];
      const field = match[2];
      if (indexStr === undefined || field === undefined) continue;
      const param = params[Number(indexStr)];
      if (!param) continue;
      const key = buildErrorTranslationKey(e.type);
      const translated = this.translate.instant(key);
      const message =
        translated && translated !== key
          ? translated
          : this.translate.instant(FALLBACK_VALIDATION_KEY);
      const bucket = result[param.id] ?? {};
      bucket[field] = message;
      result[param.id] = bucket;
    }
    this.parameterErrors.set(result);
  }

  onCancel(): void {
    if (this.isSaving()) return;
    this.onVisibleChange(false);
  }
}
